import { addDays, fromDay, isISODate, toDay, type ISODate } from "@/engine/date";
import type {
  AttachmentLink,
  ChecklistItem,
  CustomFieldDef,
  CustomFieldValue,
  ExecutionPriority,
  ExecutionStatus,
  ExecutionTask,
  LabelColor,
  PlannerSettings,
  Project,
  RecurrenceRule,
  TaskBucket,
  TaskComment,
  TaskLabel,
} from "@/engine/types";
import { canCompleteExecutionTask, gateTagsOf, normalizeGateTags, resetGateTags } from "./gate-ops";
import { newId } from "./tree-ops";

export * from "./gate-ops";

/**
 * Pure operations for Planner-style execution tasks. Nothing here touches the schedule network;
 * `linkedTaskIds` only ever point at schedule task ids and are pruned when those disappear.
 */

export const STATUS_ORDER: ExecutionStatus[] = ["notStarted", "inProgress", "blocked", "done"];
export const PRIORITY_ORDER: ExecutionPriority[] = ["urgent", "important", "medium", "low"];
export const LABEL_COLORS: LabelColor[] = ["pink", "red", "orange", "yellow", "green", "teal", "blue", "purple", "gray"];

export const STATUS_LABEL: Record<ExecutionStatus, string> = {
  notStarted: "시작 전",
  inProgress: "진행 중",
  blocked: "막힘",
  done: "완료",
};

export const PRIORITY_LABEL: Record<ExecutionPriority, string> = {
  urgent: "긴급",
  important: "중요",
  medium: "보통",
  low: "낮음",
};

export function isExecutionStatus(v: unknown): v is ExecutionStatus {
  return typeof v === "string" && (STATUS_ORDER as string[]).includes(v);
}
export function isExecutionPriority(v: unknown): v is ExecutionPriority {
  return typeof v === "string" && (PRIORITY_ORDER as string[]).includes(v);
}

export function defaultPlannerSettings(): PlannerSettings {
  return {
    buckets: [
      { id: "b_todo", name: "할 일" },
      { id: "b_week", name: "이번 주" },
      { id: "b_later", name: "나중에" },
    ],
    labels: [
      { id: "l_bug", name: "버그", color: "red" },
      { id: "l_review", name: "리뷰 필요", color: "orange" },
      { id: "l_doc", name: "문서", color: "blue" },
      { id: "l_meeting", name: "회의", color: "purple" },
    ],
    customFields: [],
  };
}

export function makeExecutionTask(partial: Partial<ExecutionTask> = {}, now = new Date().toISOString()): ExecutionTask {
  return {
    id: newId("x"),
    title: "",
    assignees: [],
    linkedTaskIds: [],
    labelIds: [],
    checklist: [],
    attachments: [],
    showChecklistOnCard: true,
    comments: [],
    activity: [],
    customFields: {},
    order: 0,
    createdAt: now,
    updatedAt: now,
    ...partial,
  };
}

/* ---------------------------------------------------------------- normalization (load/import) */

function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function normalizeTask(raw: unknown, index: number, now: string): ExecutionTask | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || !r.id) return null;
  const t = makeExecutionTask({ id: r.id, order: index }, now);
  t.title = typeof r.title === "string" ? r.title : "";
  if (isExecutionStatus(r.status)) t.status = r.status;
  if (isExecutionPriority(r.priority)) t.priority = r.priority;
  if (typeof r.bucketId === "string") t.bucketId = r.bucketId;
  t.assignees = strArray(r.assignees);
  if (isISODate(r.start)) t.start = r.start;
  if (isISODate(r.due)) t.due = r.due;
  if (typeof r.completedAt === "string") t.completedAt = r.completedAt;
  t.linkedTaskIds = strArray(r.linkedTaskIds);
  t.labelIds = strArray(r.labelIds);
  t.checklist = Array.isArray(r.checklist)
    ? r.checklist
        .filter((c): c is ChecklistItem => !!c && typeof c === "object" && typeof (c as ChecklistItem).text === "string")
        .map((c) => ({
          id: typeof c.id === "string" ? c.id : newId("ck"),
          text: c.text,
          done: !!c.done,
          ...(typeof c.assignee === "string" && c.assignee ? { assignee: c.assignee } : {}),
          ...(isISODate(c.due) ? { due: c.due } : {}),
        }))
    : [];
  if (typeof r.notes === "string") t.notes = r.notes;
  t.attachments = Array.isArray(r.attachments)
    ? r.attachments
        .filter((a): a is ExecutionTask["attachments"][number] => !!a && typeof a === "object" && typeof (a as { url?: unknown }).url === "string")
        .map((a) => ({
          id: typeof a.id === "string" ? a.id : newId("at"),
          title: typeof a.title === "string" ? a.title : a.url,
          url: a.url,
          addedAt: typeof a.addedAt === "string" ? a.addedAt : now,
          ...(a.kind === "image" || a.kind === "file" || a.kind === "link" ? { kind: a.kind } : {}),
          ...(typeof a.size === "number" ? { size: a.size } : {}),
          ...(typeof a.mimeType === "string" ? { mimeType: a.mimeType } : {}),
          ...(typeof a.addedBy === "string" && a.addedBy.trim() ? { addedBy: a.addedBy.trim() } : {}),
        }))
    : [];
  t.showChecklistOnCard = typeof r.showChecklistOnCard === "boolean" ? r.showChecklistOnCard : true;
  if (typeof r.coverAttachmentId === "string") t.coverAttachmentId = r.coverAttachmentId;
  if (r.recurrence && typeof r.recurrence === "object") {
    const rec = r.recurrence as RecurrenceRule;
    if (rec.freq === "daily" || rec.freq === "weekly" || rec.freq === "monthly") {
      t.recurrence = { freq: rec.freq, interval: Math.max(1, Math.round(Number(rec.interval) || 1)), ...(isISODate(rec.until) ? { until: rec.until } : {}) };
    }
  }
  if (typeof r.recurrenceOf === "string") t.recurrenceOf = r.recurrenceOf;
  t.comments = Array.isArray(r.comments)
    ? r.comments
        .filter((c): c is ExecutionTask["comments"][number] => !!c && typeof c === "object" && typeof (c as { body?: unknown }).body === "string")
        .map((c) => ({ id: typeof c.id === "string" ? c.id : newId("cm"), author: typeof c.author === "string" ? c.author : "", body: c.body, at: typeof c.at === "string" ? c.at : now }))
    : [];
  t.activity = Array.isArray(r.activity)
    ? r.activity
        .filter((a): a is ExecutionTask["activity"][number] => !!a && typeof a === "object" && typeof (a as { message?: unknown }).message === "string")
        .map((a) => ({ id: typeof a.id === "string" ? a.id : newId("ac"), at: typeof a.at === "string" ? a.at : now, actor: typeof a.actor === "string" ? a.actor : "", message: a.message }))
    : [];
  t.customFields = r.customFields && typeof r.customFields === "object" ? { ...(r.customFields as Record<string, CustomFieldValue>) } : {};
  const gates = normalizeGateTags(r.gateTags);
  if (gates.length) t.gateTags = gates;
  if (typeof r.originIntakeId === "string" && r.originIntakeId) t.originIntakeId = r.originIntakeId;
  t.order = typeof r.order === "number" && Number.isFinite(r.order) ? r.order : index;
  t.createdAt = typeof r.createdAt === "string" ? r.createdAt : now;
  t.updatedAt = typeof r.updatedAt === "string" ? r.updatedAt : now;
  return t;
}

function normalizeSettings(raw: unknown): PlannerSettings {
  const d = defaultPlannerSettings();
  if (!raw || typeof raw !== "object") return d;
  const r = raw as Partial<PlannerSettings>;
  const buckets = Array.isArray(r.buckets) ? r.buckets.filter((b): b is TaskBucket => !!b && typeof b.id === "string" && typeof b.name === "string") : d.buckets;
  const labels = Array.isArray(r.labels)
    ? r.labels
        .filter((l): l is TaskLabel => !!l && typeof l.id === "string" && typeof l.name === "string")
        .map((l) => ({ ...l, color: LABEL_COLORS.includes(l.color) ? l.color : "gray" }))
    : d.labels;
  const customFields = Array.isArray(r.customFields)
    ? r.customFields.filter((f): f is CustomFieldDef => !!f && typeof f.id === "string" && typeof f.name === "string" && typeof f.type === "string")
    : [];
  return { buckets, labels, customFields };
}

/**
 * Fill in Planner data on a project that may predate it (old localStorage blobs, imported JSON).
 * Dangling scope links, unknown buckets/labels and duplicate ids are dropped. Idempotent.
 */
export function normalizeExecution<T extends Partial<Project> & { tasks: Project["tasks"] }>(project: T): T & Pick<Project, "executionTasks" | "planner"> {
  const now = new Date().toISOString();
  const planner = normalizeSettings(project.planner);
  const known = new Set(project.tasks.map((t) => t.id));
  const bucketIds = new Set(planner.buckets.map((b) => b.id));
  const labelIds = new Set(planner.labels.map((l) => l.id));
  const fieldIds = new Set(planner.customFields.map((f) => f.id));
  const seen = new Set<string>();
  const list: ExecutionTask[] = [];
  const raw = Array.isArray(project.executionTasks) ? project.executionTasks : [];
  raw.forEach((x, i) => {
    const t = normalizeTask(x, i, now);
    if (!t || seen.has(t.id)) return;
    seen.add(t.id);
    t.linkedTaskIds = [...new Set(t.linkedTaskIds.filter((id) => known.has(id)))];
    t.labelIds = [...new Set(t.labelIds.filter((id) => labelIds.has(id)))];
    if (t.bucketId && !bucketIds.has(t.bucketId)) delete t.bucketId;
    for (const k of Object.keys(t.customFields)) if (!fieldIds.has(k)) delete t.customFields[k];
    list.push(t);
  });
  return { ...project, executionTasks: list, planner };
}

/* ---------------------------------------------------------------- CRUD helpers */

export function updateExecutionTask(list: ExecutionTask[], id: string, patch: Partial<ExecutionTask>, now = new Date().toISOString()): ExecutionTask[] {
  return list.map((t) => (t.id === id ? { ...t, ...patch, updatedAt: now } : t));
}

export function removeExecutionTasks(list: ExecutionTask[], ids: string[]): ExecutionTask[] {
  const gone = new Set(ids);
  return list.filter((t) => !gone.has(t.id));
}

/** Drop links to schedule tasks that no longer exist. Tasks themselves are kept (no cascade). */
export function pruneLinks(list: ExecutionTask[], existingTaskIds: Iterable<string>): ExecutionTask[] {
  const known = new Set(existingTaskIds);
  let changed = false;
  const out = list.map((t) => {
    const kept = t.linkedTaskIds.filter((id) => known.has(id));
    if (kept.length === t.linkedTaskIds.length) return t;
    changed = true;
    return { ...t, linkedTaskIds: kept };
  });
  return changed ? out : list;
}

export function duplicateExecutionTask(list: ExecutionTask[], id: string, now = new Date().toISOString()): { list: ExecutionTask[]; id: string | null } {
  const src = list.find((t) => t.id === id);
  if (!src) return { list, id: null };
  const copy: ExecutionTask = {
    ...structuredClone(src),
    id: newId("x"),
    title: src.title ? `${src.title} (복사)` : "",
    status: src.status === "done" ? "notStarted" : src.status,
    completedAt: undefined,
    checklist: src.checklist.map((c) => ({ ...c, id: newId("ck"), done: false })),
    comments: [],
    activity: [],
    recurrenceOf: undefined,
    ...(gateTagsOf(src).length ? { gateTags: resetGateTags(src.gateTags) } : {}),
    order: src.order + 0.5,
    createdAt: now,
    updatedAt: now,
  };
  const idx = list.findIndex((t) => t.id === id);
  return { list: [...list.slice(0, idx + 1), copy, ...list.slice(idx + 1)], id: copy.id };
}

export function logActivity(task: ExecutionTask, actor: string, message: string, now = new Date().toISOString()): ExecutionTask {
  return { ...task, activity: [{ id: newId("ac"), at: now, actor, message }, ...task.activity].slice(0, 200) };
}

/** Status change with completion bookkeeping and (when completing a recurring task) the next occurrence. */
export function setStatus(list: ExecutionTask[], id: string, status: ExecutionStatus | undefined, actor: string, now = new Date().toISOString()): { list: ExecutionTask[]; spawnedId: string | null } {
  const cur = list.find((t) => t.id === id);
  if (!cur) return { list, spawnedId: null };
  if (status === "done" && !canCompleteExecutionTask(cur).allowed) return { list, spawnedId: null }; // Hard Lock
  let next: ExecutionTask = { ...cur, status, updatedAt: now };
  if (status === "done") next.completedAt = now;
  else delete next.completedAt;
  next = logActivity(next, actor, `상태 → ${status ? STATUS_LABEL[status] : "미정"}`, now);
  let out = list.map((t) => (t.id === id ? next : t));
  let spawnedId: string | null = null;
  if (status === "done" && cur.status !== "done" && cur.recurrence) {
    const occurrence = nextOccurrence(cur, now);
    if (occurrence) {
      out = [...out, occurrence];
      spawnedId = occurrence.id;
    }
  }
  return { list: out, spawnedId };
}

/* ---------------------------------------------------------------- recurrence */

function shift(iso: ISODate, rule: RecurrenceRule): ISODate {
  const day = toDay(iso);
  if (rule.freq === "daily") return fromDay(day + rule.interval);
  if (rule.freq === "weekly") return fromDay(day + 7 * rule.interval);
  // monthly: clamp to the last day of the target month (1/31 → 2/28) instead of rolling over
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7)) - 1 + rule.interval;
  const d = Number(iso.slice(8, 10));
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return fromDay(Math.floor(Date.UTC(y, m, Math.min(d, lastDay)) / 86_400_000));
}

/** Build the next occurrence of a recurring task. Returns null when the rule has expired. */
export function nextOccurrence(task: ExecutionTask, now = new Date().toISOString()): ExecutionTask | null {
  const rule = task.recurrence;
  if (!rule) return null;
  const anchor = task.due ?? task.start ?? now.slice(0, 10);
  const nextDue = shift(anchor, rule);
  if (rule.until && nextDue > rule.until) return null;
  const span = task.start && task.due ? toDay(task.due) - toDay(task.start) : 0;
  return makeExecutionTask(
    {
      title: task.title,
      status: "notStarted",
      priority: task.priority,
      bucketId: task.bucketId,
      assignees: [...task.assignees],
      start: task.start ? addDays(nextDue, -span) : undefined,
      due: task.due ? nextDue : undefined,
      linkedTaskIds: [...task.linkedTaskIds],
      labelIds: [...task.labelIds],
      checklist: task.checklist.map((c) => ({ ...c, id: newId("ck"), done: false })),
      notes: task.notes,
      attachments: task.attachments.map((a) => ({ ...a })),
      recurrence: { ...rule },
      recurrenceOf: task.recurrenceOf ?? task.id,
      customFields: { ...task.customFields },
      ...(gateTagsOf(task).length ? { gateTags: resetGateTags(task.gateTags) } : {}),
      order: task.order + 0.5,
    },
    now,
  );
}

export function describeRecurrence(rule: RecurrenceRule | undefined): string {
  if (!rule) return "반복 없음";
  const unit = rule.freq === "daily" ? "일" : rule.freq === "weekly" ? "주" : "개월";
  const every = rule.interval === 1 ? `매${rule.freq === "monthly" ? "월" : rule.freq === "weekly" ? "주" : "일"}` : `${rule.interval}${unit}마다`;
  return rule.until ? `${every} · ${rule.until}까지` : every;
}

/* ---------------------------------------------------------------- checklist / comments / attachments */

export function addChecklistItem(task: ExecutionTask, text: string, extra?: { assignee?: string; due?: ISODate }): ExecutionTask {
  const t = text.trim();
  if (!t) return task;
  return {
    ...task,
    checklist: [
      ...task.checklist,
      {
        id: newId("ck"),
        text: t,
        done: false,
        ...(extra?.assignee ? { assignee: extra.assignee } : {}),
        ...(extra?.due ? { due: extra.due } : {}),
      },
    ],
  };
}

export function updateChecklistItem(task: ExecutionTask, itemId: string, patch: Partial<ChecklistItem>): ExecutionTask {
  return { ...task, checklist: task.checklist.map((c) => (c.id === itemId ? { ...c, ...patch } : c)) };
}

export function removeChecklistItem(task: ExecutionTask, itemId: string): ExecutionTask {
  return { ...task, checklist: task.checklist.filter((c) => c.id !== itemId) };
}

export function convertChecklistToTask(
  list: ExecutionTask[],
  taskId: string,
  itemId: string,
  now = new Date().toISOString(),
): { list: ExecutionTask[]; newTask: ExecutionTask | null } {
  const parentIdx = list.findIndex((t) => t.id === taskId);
  if (parentIdx < 0) return { list, newTask: null };
  const parent = list[parentIdx]!;
  const item = parent.checklist.find((c) => c.id === itemId);
  if (!item) return { list, newTask: null };

  const remainingChecklist = parent.checklist.filter((c) => c.id !== itemId);
  const updatedParent: ExecutionTask = {
    ...parent,
    checklist: remainingChecklist,
    updatedAt: now,
  };

  const newTask = makeExecutionTask(
    {
      title: item.text,
      status: item.done ? "done" : "notStarted",
      completedAt: item.done ? now : undefined,
      bucketId: parent.bucketId,
      assignees: item.assignee ? [item.assignee] : [...parent.assignees],
      due: item.due ?? parent.due,
      start: parent.start,
      linkedTaskIds: [...parent.linkedTaskIds],
      order: parent.order + 0.5,
    },
    now,
  );

  const nextList = [
    ...list.slice(0, parentIdx),
    updatedParent,
    newTask,
    ...list.slice(parentIdx + 1),
  ];
  return { list: nextList, newTask };
}

export function checklistProgress(task: ExecutionTask): { done: number; total: number } {
  return { done: task.checklist.filter((c) => c.done).length, total: task.checklist.length };
}

type Commentable = { comments: TaskComment[] };

export function addComment<T extends Commentable>(
  task: T,
  author: string,
  body: string,
  options?: { parentId?: string; attachments?: AttachmentLink[] },
  now = new Date().toISOString(),
): T {
  const b = body.trim();
  const hasAttachments = options?.attachments && options.attachments.length > 0;
  if (!b && !hasAttachments) return task;
  return {
    ...task,
    comments: [
      ...task.comments,
      {
        id: newId("cm"),
        author,
        body: b,
        at: now,
        ...(options?.parentId ? { parentId: options.parentId } : {}),
        ...(hasAttachments ? { attachments: options.attachments } : {}),
      },
    ],
  };
}

export function updateComment<T extends Commentable>(
  task: T,
  commentId: string,
  body: string,
  options?: { attachments?: AttachmentLink[] },
  now = new Date().toISOString(),
): T {
  const b = body.trim();
  const nextAttachments = options?.attachments;
  const hasAttachments = nextAttachments && nextAttachments.length > 0;
  if (!b && !hasAttachments && nextAttachments === undefined) return task;
  return {
    ...task,
    comments: task.comments.map((c) =>
      c.id === commentId
        ? {
            ...c,
            body: b,
            updatedAt: now,
            ...(nextAttachments !== undefined ? { attachments: nextAttachments.length ? nextAttachments : undefined } : {}),
          }
        : c,
    ),
  };
}

export function deleteComment<T extends Commentable>(
  task: T,
  commentId: string,
  now = new Date().toISOString(),
): T {
  return {
    ...task,
    comments: task.comments.map((c) =>
      c.id === commentId
        ? {
            ...c,
            deleted: true,
            deletedAt: now,
            body: "",
            attachments: undefined,
          }
        : c,
    ),
  };
}

export function addAttachment(
  task: ExecutionTask,
  title: string,
  url: string,
  extra?: { kind?: "link" | "file" | "image"; size?: number; mimeType?: string; addedBy?: string },
  now = new Date().toISOString(),
): ExecutionTask {
  const u = url.trim();
  if (!u) return task;
  const isImg =
    extra?.kind === "image" ||
    (extra?.mimeType && extra.mimeType.startsWith("image/")) ||
    /\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(u) ||
    u.startsWith("data:image/");
  const kind = extra?.kind ?? (isImg ? "image" : u.startsWith("data:") ? "file" : "link");
  return {
    ...task,
    attachments: [
      ...task.attachments,
      {
        id: newId("at"),
        title: title.trim() || u,
        url: u,
        addedAt: now,
        kind,
        ...(extra?.addedBy?.trim() ? { addedBy: extra.addedBy.trim() } : {}),
        ...(extra?.size !== undefined ? { size: extra.size } : {}),
        ...(extra?.mimeType ? { mimeType: extra.mimeType } : {}),
      },
    ],
  };
}

export function removeAttachment(task: ExecutionTask, attachmentId: string): ExecutionTask {
  return {
    ...task,
    attachments: task.attachments.filter((a) => a.id !== attachmentId),
    coverAttachmentId: task.coverAttachmentId === attachmentId ? undefined : task.coverAttachmentId,
  };
}

/* ---------------------------------------------------------------- ordering inside a column */

/**
 * Place `id` in `bucketId` right before `beforeId` (or at the end). Works for any grouping axis:
 * callers pass the ids of the destination column in display order.
 */
export function reorderWithin(list: ExecutionTask[], id: string, columnIds: string[], beforeId: string | null, now = new Date().toISOString()): ExecutionTask[] {
  const others = columnIds.filter((x) => x !== id);
  const at = beforeId ? others.indexOf(beforeId) : -1;
  const seq = at < 0 ? [...others, id] : [...others.slice(0, at), id, ...others.slice(at)];
  const orderOf = new Map(seq.map((x, i) => [x, i]));
  return list.map((t) => (orderOf.has(t.id) ? { ...t, order: orderOf.get(t.id)!, updatedAt: t.id === id ? now : t.updatedAt } : t));
}

/* ---------------------------------------------------------------- planner settings */

export function addBucket(settings: PlannerSettings, name: string): { settings: PlannerSettings; id: string } {
  const id = newId("b");
  return { settings: { ...settings, buckets: [...settings.buckets, { id, name: name.trim() || "새 버킷" }] }, id };
}

export function renameBucket(settings: PlannerSettings, id: string, name: string): PlannerSettings {
  return { ...settings, buckets: settings.buckets.map((b) => (b.id === id ? { ...b, name: name.trim() || b.name } : b)) };
}

export function moveBucket(settings: PlannerSettings, id: string, direction: -1 | 1): PlannerSettings {
  const i = settings.buckets.findIndex((b) => b.id === id);
  const j = i + direction;
  if (i < 0 || j < 0 || j >= settings.buckets.length) return settings;
  const buckets = [...settings.buckets];
  [buckets[i], buckets[j]] = [buckets[j]!, buckets[i]!];
  return { ...settings, buckets };
}

function moveTo<T extends { id: string }>(list: T[], id: string, toIndex: number): T[] {
  const from = list.findIndex((x) => x.id === id);
  if (from < 0 || toIndex < 0 || toIndex >= list.length || from === toIndex) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(toIndex, 0, item!);
  return next;
}

export function reorderBucket(settings: PlannerSettings, id: string, toIndex: number): PlannerSettings {
  const buckets = moveTo(settings.buckets, id, toIndex);
  return buckets === settings.buckets ? settings : { ...settings, buckets };
}

export function reorderLabel(settings: PlannerSettings, id: string, toIndex: number): PlannerSettings {
  const labels = moveTo(settings.labels, id, toIndex);
  return labels === settings.labels ? settings : { ...settings, labels };
}

/** Remove a bucket; its tasks keep existing with no bucket. */
export function removeBucket(project: Pick<Project, "planner" | "executionTasks">, id: string): Pick<Project, "planner" | "executionTasks"> {
  return {
    planner: { ...project.planner, buckets: project.planner.buckets.filter((b) => b.id !== id) },
    executionTasks: project.executionTasks.map((t) => (t.bucketId === id ? { ...t, bucketId: undefined } : t)),
  };
}

export function addLabel(settings: PlannerSettings, name: string, color: LabelColor): { settings: PlannerSettings; id: string } {
  const id = newId("l");
  return { settings: { ...settings, labels: [...settings.labels, { id, name: name.trim() || "라벨", color }] }, id };
}

export function updateLabel(settings: PlannerSettings, id: string, patch: Partial<TaskLabel>): PlannerSettings {
  return { ...settings, labels: settings.labels.map((l) => (l.id === id ? { ...l, ...patch, id } : l)) };
}

export function removeLabel(project: Pick<Project, "planner" | "executionTasks">, id: string): Pick<Project, "planner" | "executionTasks"> {
  return {
    planner: { ...project.planner, labels: project.planner.labels.filter((l) => l.id !== id) },
    executionTasks: project.executionTasks.map((t) => (t.labelIds.includes(id) ? { ...t, labelIds: t.labelIds.filter((x) => x !== id) } : t)),
  };
}

export function addCustomField(settings: PlannerSettings, def: Omit<CustomFieldDef, "id">): { settings: PlannerSettings; id: string } {
  const id = newId("cf");
  return { settings: { ...settings, customFields: [...settings.customFields, { ...def, id, name: def.name.trim() || "필드" }] }, id };
}

export function updateCustomField(settings: PlannerSettings, id: string, patch: Partial<CustomFieldDef>): PlannerSettings {
  return { ...settings, customFields: settings.customFields.map((f) => (f.id === id ? { ...f, ...patch, id } : f)) };
}

export function removeCustomField(project: Pick<Project, "planner" | "executionTasks">, id: string): Pick<Project, "planner" | "executionTasks"> {
  return {
    planner: { ...project.planner, customFields: project.planner.customFields.filter((f) => f.id !== id) },
    executionTasks: project.executionTasks.map((t) => {
      if (!(id in t.customFields)) return t;
      const { [id]: _drop, ...rest } = t.customFields;
      return { ...t, customFields: rest };
    }),
  };
}

/* ---------------------------------------------------------------- derived facts */

export function isOverdue(task: ExecutionTask, today: ISODate): boolean {
  return !!task.due && task.status !== "done" && task.due < today;
}

/** Fields the user has not decided yet — Planner lets you create first and fill in later. */
export function undecided(task: ExecutionTask): Array<"status" | "assignee" | "date" | "scope"> {
  const out: Array<"status" | "assignee" | "date" | "scope"> = [];
  if (!task.status) out.push("status");
  if (!task.assignees.length) out.push("assignee");
  if (!task.start && !task.due) out.push("date");
  if (!task.linkedTaskIds.length) out.push("scope");
  return out;
}

/** Distinct assignee names across schedule, execution tasks, and project resource assignments, most used first. */
export function knownAssignees(project: Pick<Project, "tasks" | "executionTasks"> & { resourceAssignments?: Array<{ resourceId: string }> }): string[] {
  const count = new Map<string, number>();
  for (const t of project.tasks) if (t.assignee) count.set(t.assignee, (count.get(t.assignee) ?? 0) + 1);
  for (const x of project.executionTasks) for (const a of x.assignees) count.set(a, (count.get(a) ?? 0) + 1);
  return [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "ko")).map(([n]) => n);
}
